import React, { useCallback, useState } from 'react';
import { Button, Modal, Spin } from 'antd';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { FormEditService } from './FormEditService';
import { InputTime } from './styles';
import ModalDataRepeatAppointments from './repeatAppointments/ModalDataRepeatAppointments';
import ContentMobileForm from './ContentMobileForm';
import { useDictionaryTranslation } from '../../../hooks/useDictionaryTranslation';
import { FormWrapper } from '../../../components/formWrapper/form-wrapper';
import { useServiceData } from '../hooks/useServiceData';
import { useNotifications } from '../../../hooks/useNotifications';
import { profileTypes } from '../../../config/constants/userConstants';
import { ExtendedFormProvider } from '../../../components/mobileForm/context/mobileFormContext';
import { mobileFormTypes } from '../../../components/mobileForm/hooks/useExtendedFormActions';
import useMobileForm from '../../../components/mobileForm/hooks/useMobileForm';
import { serviceSolutionRules } from '../../../config/constants/serviceSolutionRulesConstants';

const ModalEditService = ({
  show,
  handleClose,
  serviceId,
  defaultData,
  newData,
  selectedProfile,
  awaitingListEntryId,
}) => {
  const { showAllowCreationNotification } = useNotifications();
  const [extendedFormId, setExtendedFormId] = useState();
  const [dateValues, setDateValues] = useState();
  const [modalData, setModalData] = useState({ show: false, serviceData: null });

  const { translate: t } = useDictionaryTranslation();
  const { serviceData, createService, updateService, isLoading } = useServiceData({
    serviceId,
    defaultData,
    newData,
  });

  const mobileForm = useMobileForm({
    idService: serviceId,
    idApp: extendedFormId,
    values: serviceData?.extendedFormData,
    itemType: mobileFormTypes.service,
  });

  const selectedProfileId = selectedProfile ?? serviceData?.idProfile ?? profileTypes.TECHNICIAN.id;
  const { solutionId, currentUser } = useSelector((state) => ({
    solutionId: state.auth.accountData.solutionId,
    currentUser: state.auth.currentUser,
  }));

  const currentSolutionRules = serviceSolutionRules[solutionId] ?? serviceSolutionRules.default;

  const handleCloseModalRepeat = () => {
    setModalData({ show: false, serviceData: null });
  };

  const closeModal = useCallback(
    (refreshData = false) => {
      setExtendedFormId(undefined);
      handleClose(refreshData);
    },
    [handleClose],
  );

  const getBaseServiceData = (formValues, dates) => {
    let startDates = [];
    if (dates?.data?.datesAvailability && Array.isArray(dates.data.datesAvailability)) {
      startDates = dates.data.datesAvailability
        .filter((date) => date.isAvailable)
        .map((date) => moment(date.date).toISOString());
    } else {
      startDates = moment(`${formValues.serviceDate} ${formValues.serviceTime}`, 'YYYY-MM-DD hh:mm')
        .toDate()
        .toISOString();
    }
    const collaborators = formValues?.collaborators?.map((collaborator) => collaborator?.value);

    const baseServiceData = {
      start: startDates,
      idTypeService: formValues.serviceType._id,
      idAsset: formValues.selectedAsset,
      idAssignedCompany: formValues.assignedCompany ? formValues.assignedCompany._id : null,
      idContactWhoCalls: formValues.selectedContact ? formValues.selectedContact._id : null,
      idFinalCustomer: formValues.selectedFinalCustomer?._id,
      idUser:
        currentUser.profile.idProfile === profileTypes.TECHNICIAN.id
          ? currentUser._id
          : formValues.selectedTechnician?._id ?? null,
      collaborators,
      name: formValues.serviceName,
      address: formValues.address?.address,
      markerCoords: formValues.address?.markerCoords,
      servicePriority: formValues.servicePriority?.value,
      title: formValues.serviceDescription,
      reference: formValues.reference,
      idHeadquarter: formValues.headquarter?._id,
      idProfile: selectedProfileId,
      from: formValues.origin?.address,
      markerFromCoords: formValues.origin?.markerCoords,
      repeatInfo: {
        repeatTimes: formValues.repeatTimes,
        timeUnit: formValues.timeUnit,
        howOften: formValues.howOften,
      },
    };

    if (baseServiceData.address == null) {
      if (currentSolutionRules.useContactAddress) {
        baseServiceData.address = formValues.selectedContact?.address;
        baseServiceData.markerCoords = formValues.selectedContact?.markerCoords;
      }
      if (currentSolutionRules.useHeadquartersAddress) {
        baseServiceData.address = formValues.headquarter?.address;
        baseServiceData.markerCoords = formValues.headquarter?.markerCoords;
      }
    }

    return baseServiceData;
  };

  const saveServiceData = async ({ newServiceId, newValues, showMessage }) => {
    if (extendedFormId) {
      const resultFormData = await mobileForm.getStructure(true, newServiceId);
      newValues ??= {};
      newValues.extendedFormData = resultFormData?.resultStructure;
    }
    if (!newValues) return null;
    return updateService(newValues, newServiceId, showMessage);
  };

  const createIncident = async (dataToSend) => {
    const newServiceData = await createService(dataToSend);
    if (!newServiceData?.incident?._id) return;
    await saveServiceData({
      newServiceId: newServiceData.incident._id,
      showMessage: false,
    });
  };

  const handleSubmit = async (formValues) => {
    const resultValidations = mobileForm?.values?.updateFormValues(true);
    if (resultValidations?.hasError) {
      return;
    }

    setDateValues(formValues);

    const dataToSend = {
      ...getBaseServiceData(formValues),
      awaitingListEntryId,
    };

    if (serviceId) {
      await saveServiceData({
        newServiceId: serviceId,
        newValues: dataToSend,
        showMessage: true,
      });
      closeModal(true);
      return;
    }

    if (formValues.checkRepeatAppointment) {
      setModalData({ show: true, serviceData: dataToSend });
      return;
    }

    if (!dataToSend.idUser) {
      showAllowCreationNotification({
        title: 'areYouSure',
        content: 'allowCreation',
        onOk: async () => {
          await createIncident(dataToSend);
          closeModal(true);
        },
      });
      return;
    }
    await createIncident(dataToSend);
    closeModal(true);
  };

  const handleSubmitRepeat = async (incidentData, dates) => {
    const dataToSend = { ...incidentData, repeatDates: dates };
    const { createdServices } = await createService(dataToSend);
    await Promise.all(
      createdServices.map((item) => {
        return saveServiceData({
          newServiceId: item._id,
          showMessage: false,
        });
      }),
    );
    closeModal(true);
  };

  const onChangeBaseData = useCallback((formValues = {}) => {
    if ('serviceType' in formValues) {
      setExtendedFormId(formValues.serviceType?.isExtendedFormEnabled ? formValues.serviceType?.idExtendedForm : null);
    }
  }, []);

  return (
    <>
      <Modal
        destroyOnClose
        title={t(serviceId ? 'editService' : 'createService')}
        centered
        width="90%"
        maskClosable={false}
        onCancel={closeModal}
        open={show}
        footer={
          <div>
            <Button form="createService" key="submit" htmlType="submit" type="primary" disabled={isLoading}>
              {t('save')}
            </Button>
          </div>
        }
      >
        <Spin spinning={isLoading}>
          <FormWrapper>
            <InputTime>
              <FormEditService
                key={serviceId && !serviceData ? '1' : '2'}
                isEditing={Boolean(serviceId)}
                formName="createService"
                serviceData={serviceData}
                handleSubmit={handleSubmit}
                onChangeValues={onChangeBaseData}
                selectedProfile={selectedProfileId}
              />

              <ExtendedFormProvider itemId={serviceId} itemType={mobileFormTypes.service}>
                <ContentMobileForm mobileForm={mobileForm} />
              </ExtendedFormProvider>
            </InputTime>
          </FormWrapper>
        </Spin>
      </Modal>

      {modalData.show ? (
        <ModalDataRepeatAppointments
          serviceId={serviceId}
          cancelModalRepeat={handleCloseModalRepeat}
          isOpen={modalData.show}
          incidentData={modalData.serviceData}
          handleSubmitRepeat={handleSubmitRepeat}
          dateValues={dateValues}
          getExtendedFormValues={modalData.getExtendedFormValues}
        />
      ) : null}
    </>
  );
};

ModalEditService.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  serviceId: PropTypes.string,
  defaultData: PropTypes.shape({
    start: PropTypes.string,
    headquarter: PropTypes.object,
    serviceType: PropTypes.object,
    professional: PropTypes.object,
  }),
  newData: PropTypes.shape({
    start: PropTypes.instanceOf(Date),
    headquarter: PropTypes.object,
    serviceType: PropTypes.object,
    professional: PropTypes.object,
  }),
  selectedProfile: PropTypes.string,
  awaitingListEntryId: PropTypes.string,
};

export { ModalEditService };
